import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { fetchBalance, fetchEntries, fetchBalanceEntries } from '../../services/dataService';
import html2canvas from 'html2canvas'; // Make sure to install this package
const DairyFinancialReport = (props) => {
  const [milkTableData, setMilkTableData] = useState({});
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [borrow, setBorrowData] = useState([]);
  const { userId, startDate, endDate, userName, userMobile } = props;
// Single source of truth for print styles
const PRINT_STYLES = `
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 20px;
      color: #000;
      background: #fff;
    }
    .print-header {
      text-align: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #4f46e5;
      padding-bottom: 10px;
    }
    .report-title {
      font-size: 24px;
      font-weight: bold;
      color: #4f46e5;
      margin-bottom: 5px;
    }
    .report-details {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      margin-bottom: 15px;
      font-size: 14px;
    }
    .report-details div {
      margin: 5px 10px;
    }
    .vintage-report {
      width: 100%;
      max-width: 100%;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    th {
      background-color: #f2f2f2;
      font-weight: bold;
    }
    .text-right {
      text-align: right;
    }
    .text-center {
      text-align: center;
    }
    h2 {
      color: #4f46e5;
      font-size: 20px;
      margin: 15px 0 10px;
    }
    @page {
      size: auto;
      margin: 10mm;
    }
    @media print {
      body {
        padding: 0;
      }
      .no-print {
        display: none !important;
      }
    }
  </style>
`;
  const fetchData = async () => {
    try {
      setLoading(true);
      const { morningEntries, eveningEntries } = await fetchEntries(startDate, endDate, userId);
      const balanceData = await fetchBalance(startDate, endDate, userId);
      const sheetData = await fetchBalanceEntries(startDate, endDate, userId);
      setBorrowData(sheetData.borrow);

      // Process milk data
      const processMilkEntries = (entries, session) => 
        entries.map(entry => ({
          id: entry.id,
          type: "milk",
          date: entry.date,
          session: session,
          weight: parseFloat(entry.weight),
          fat: parseFloat(entry.fat),
          pricePerKg: parseFloat(entry.price),
          amount: parseFloat(entry.total)
        }));

      const allMilkEntries = [
        ...processMilkEntries(eveningEntries, "evening"),
        ...processMilkEntries(morningEntries, "morning")
      ];

      // Group by date and session
      const milkGroups = {};
      allMilkEntries.forEach(entry => {
        const key = entry.date + '|' + entry.session;
        if (!milkGroups[key]) {
          milkGroups[key] = {
            date: entry.date,
            session: entry.session,
            totalWeight: 0,
            totalAmount: 0,
            weightedFat: 0,
          };
        }
        milkGroups[key].totalWeight += entry.weight;
        milkGroups[key].totalAmount += entry.amount;
        milkGroups[key].weightedFat += entry.weight * entry.fat;
      });

      // Calculate averages and group by date
      const milkByDate = {};
      Object.values(milkGroups).forEach(group => {
        const date = group.date;
        if (!milkByDate[date]) milkByDate[date] = [];
        
        milkByDate[date].push({
          ...group,
          avgFat: group.weightedFat / group.totalWeight,
          pricePerKg: group.totalAmount / group.totalWeight
        });
      });

      // Sort sessions within each date
      Object.keys(milkByDate).forEach(date => {
        milkByDate[date].sort((a, b) => 
          a.session === 'morning' ? -1 : b.session === 'morning' ? 1 : 0
        );
      });

      setBalanceData(balanceData);
      setMilkTableData(milkByDate);
    } catch (error) {
      console.error("Fetch error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };



  const handlePrint = async () => {
    const hasBridge = window.AndroidBridge && typeof window.AndroidBridge.printHtml === 'function';
    const printableArea = document.getElementById('printable-area');
    if (!printableArea) return;
  
    // Generate complete HTML document with print styles
    const printHtmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Financial Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              color: #000;
              background: #fff;
              width: 100%;
            }
            @media screen {
              body {
                padding: 20px;
              }
            }
            @media print {
              body {
                padding: 0;
              }
              .no-print {
                display: none !important;
              }
            }
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #4f46e5;
              padding-bottom: 10px;
            }
            .report-title {
              font-size: 24px;
              font-weight: bold;
              color: #4f46e5;
              margin-bottom: 5px;
            }
            .report-details {
              display: flex;
              justify-content: space-between;
              flex-wrap: wrap;
              margin-bottom: 15px;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              page-break-inside: avoid;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            @page {
              size: A4;
              margin: 10mm;
            }
            @media print {
              body {
                width: 210mm; /* A4 width */
                height: 297mm; /* A4 height */
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${printableArea.innerHTML}
        </body>
      </html>
    `;
  
    if (hasBridge) {
      // Pass HTML directly to Android for native rendering
      window.AndroidBridge.printHtml(printHtmlContent);
    } else {
      // Fallback for browser printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printHtmlContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchData();
  }, [startDate, endDate, userId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 to-indigo-100">
        <div className="w-16 h-16 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-lg font-semibold text-indigo-600">
          Loading financial report...
        </p>
      </div>
    );
  }
  if (error) return <div className="text-center text-red-500 py-8">Error: {error}</div>;
  if (!balanceData) return <div className="text-center py-8">No data available</div>;

  // Calculate totals
  const milkTotal = balanceData.milk?.total || 0;
  const feedTotal = balanceData.feed?.totalMoney || 0;
  const moneyReceivedTotal = balanceData.moneyReceived?.totalMoney || 0;
  const moneyGivenTotal = balanceData.moneyGiven?.totalMoney || 0;
  const gheeTotal = balanceData.ghee?.totalMoney || 0;
  const openingBalance = balanceData.Before?.total || 0;
  
  const closingBalance = openingBalance + 
                       milkTotal - 
                       moneyReceivedTotal - 
                       feedTotal + 
                       moneyGivenTotal - 
                       gheeTotal;

  // Get sorted dates
  const sortedDates = Object.keys(milkTableData).sort();

  return (
    <div className="md:p-4">
      <div className="flex justify-end mb-4 no-print">
        <button
          onClick={handlePrint}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out"
        >
          Print Report
        </button>
      </div>

      <div id="printable-area" className="vintage-report bg-white p-4 md:p-6 rounded-lg border-2 border-indigo-300 shadow-lg">
  {/* In your React component */}
<div className="print-header flex flex-col md:flex-row justify-between items-start md:items-center border-b-2 border-gray-300 mb-6 pb-4 print:border-black print:mb-8 print:pb-6">
  <h1 className="report-title text-3xl font-serif font-bold text-gray-800 mb-4 md:mb-0 print:text-4xl">
    Financial Report
  </h1>
  <div className="report-details text-gray-700 space-y-1 text-sm md:text-base print:text-base">
    <div>
      <span className="font-semibold">Name:</span> {userName || 'N/A'}
    </div>
    <div>
      <span className="font-semibold">Mobile:</span> {userMobile || 'N/A'}
    </div>
    <div>
      <span className="font-semibold">Period:</span>{' '}
      {format(new Date(startDate), 'dd MMM yyyy')} – {format(new Date(endDate), 'dd MMM yyyy')}
    </div>
  </div>
</div>


        {/* Milk Entries Table */}
        <div className="mb-8">
          <h2 className="text-xl font-serif font-semibold text-indigo-700 mb-3">Milk Entries</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-indigo-100">
                  <th className="border border-indigo-300 p-2">Date</th>
                  <th className="border border-indigo-300 p-2">Session</th>
                  <th className="border border-indigo-300 p-2">Weight (kg)</th>
                  <th className="border border-indigo-300 p-2">Fat (%)</th>
                  <th className="border border-indigo-300 p-2">Price/Kg (₹)</th>
                  <th className="border border-indigo-300 p-2">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {sortedDates.map(date => {
                  const sessions = milkTableData[date];
                  return sessions.map((session, index) => (
                    <tr 
                      key={`${date}-${session.session}`} 
                      className={index % 2 === 0 ? 'bg-indigo-50' : 'bg-white'}
                    >
                      {index === 0 && (
                        <td 
                          rowSpan={sessions.length} 
                          className="border border-indigo-200 p-2 text-center font-semibold"
                        >
                          {format(parseISO(date), 'dd MMM yyyy')}
                        </td>
                      )}
                      <td className="border border-indigo-200 p-2 capitalize">{session.session}</td>
                      <td className="border border-indigo-200 p-2 text-right">{session.totalWeight.toFixed(2)}</td>
                      <td className="border border-indigo-200 p-2 text-right">{session.avgFat.toFixed(2)}</td>
                      <td className="border border-indigo-200 p-2 text-right">{session.pricePerKg.toFixed(2)}</td>
                      <td className="border border-indigo-200 p-2 text-right">{session.totalAmount.toFixed(2)}</td>
                    </tr>
                  ));
                })}
              </tbody>
              <tfoot>
                <tr className="bg-indigo-200 font-semibold">
                  <td colSpan="2" className="border border-indigo-300 p-2 text-center">Total Milk</td>
                  <td className="border border-indigo-300 p-2 text-right">
                    {balanceData.milk?.totalMilk?.toFixed(2) || '0.00'}
                  </td>
                  <td className="border border-indigo-300 p-2 text-right">-</td>
                  <td className="border border-indigo-300 p-2 text-right">-</td>
                  <td className="border border-indigo-300 p-2 text-right">
                    {balanceData.milk?.total?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Other Transactions */}
        <div className="mb-8">
          <h2 className="text-xl font-serif font-semibold text-indigo-700 mb-3">Transaction Details</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-indigo-100">
                  <th className="border border-indigo-300 p-2">Date</th>
                  <th className="border border-indigo-300 p-2">Transaction</th>
                  <th className="border border-indigo-300 p-2">Quantity</th>
                  <th className="border border-indigo-300 p-2">Price</th>
                  <th className="border border-indigo-300 p-2">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {borrow.map((transaction, index) => {
                  let description = transaction.item;
                  if (transaction.item === "Money Given") {
                    description = transaction.name 
                      ? `Money Given to ${transaction.name}`
                      : "Money Given";
                  } 
                  else if (transaction.item === "Money Received") {
                    description = transaction.name 
                      ? `Money Received from ${transaction.name}`
                      : "Money Received";
                  } 
                  else if (transaction.name) {
                    description = `${transaction.item} Purchased from ${transaction.name}`;
                  }

                  return (
                    <tr 
                      key={transaction.id} 
                      className={index % 2 === 0 ? "bg-indigo-50" : "bg-white"}
                    >
                      <td className="border border-indigo-200 p-2">{transaction.date}</td>
                      <td className="border border-indigo-200 p-2">{description}</td>
                      <td className="border border-indigo-200 p-2 text-right">
                        {transaction.quantity || '-'}
                      </td>
                      <td className="border border-indigo-200 p-2 text-right">
                        {transaction.price ? `₹${parseFloat(transaction.price).toFixed(2)}` : '-'}
                      </td>
                      <td className="border border-indigo-200 p-2 text-right">
                        ₹{parseFloat(transaction.money).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Account Summary */}
        <div>
          <h2 className="text-xl font-serif font-semibold text-indigo-700 mb-3">Account Summary</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-indigo-100">
                  <th className="border border-indigo-300 p-2">Description</th>
                  <th className="border border-indigo-300 p-2">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-indigo-50">
                  <td className="border border-indigo-200 p-2">Previous Balance</td>
                  <td className="border border-indigo-200 p-2 text-right">
                    {openingBalance.toFixed(2)}
                  </td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-indigo-200 p-2">Milk Income</td>
                  <td className="border border-indigo-200 p-2 text-right">
                     {milkTotal.toFixed(2)}
                  </td>
                </tr>
                <tr className="bg-indigo-50">
                  <td className="border border-indigo-200 p-2">Feed Expenses</td>
                  <td className="border border-indigo-200 p-2 text-right">
                    - {feedTotal.toFixed(2)}
                  </td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-indigo-200 p-2">Money Received</td>
                  <td className="border border-indigo-200 p-2 text-right">
                    + {moneyReceivedTotal.toFixed(2)}
                  </td>
                </tr>
                <tr className="bg-indigo-50">
                  <td className="border border-indigo-200 p-2">Money Given</td>
                  <td className="border border-indigo-200 p-2 text-right">
                    - {moneyGivenTotal.toFixed(2)}
                  </td>
                </tr>
                <tr className="bg-white">
                  <td className="border border-indigo-200 p-2">Ghee Expenses</td>
                  <td className="border border-indigo-200 p-2 text-right">
                    - {gheeTotal.toFixed(2)}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-indigo-200 font-semibold">
                  <td className="border border-indigo-300 p-2">Final Balance</td>
                  <td className="border border-indigo-300 p-2 text-right">
                    {closingBalance.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DairyFinancialReport;